import { simulateInflation } from './inflationModel';

/** Calendar year at which the plan reaches `age`. */
export function planCalendarYear(currentAge, age, baseCalendarYear = new Date().getFullYear()) {
  return baseCalendarYear + (age - currentAge);
}

/** Map calendar year → annual inflation rate (%). */
export function buildInflationPathMap(projection) {
  const map = new Map();
  for (const { year, value } of projection) {
    map.set(year, value);
  }
  return map;
}

export function getInflationRatePct(pathMap, calendarYear, flatRatePct) {
  if (!pathMap) return flatRatePct;
  const v = pathMap.get(calendarYear);
  return v !== undefined ? v : flatRatePct;
}

export function getNominalGrowthDecimal(
  realGrowthPct,
  pathMap,
  calendarYear,
  flatInflationPct,
) {
  const inflation = getInflationRatePct(pathMap, calendarYear, flatInflationPct);
  return (realGrowthPct + inflation) / 100;
}

/** Compound inflation from plan start through `yearsFromNow` full years. */
export function cumulativeInflationFactor({
  yearsFromNow,
  pathMap,
  flatInflationPct,
  baseCalendarYear = new Date().getFullYear(),
}) {
  if (yearsFromNow <= 0) return 1;
  if (!pathMap) {
    return (1 + flatInflationPct / 100) ** yearsFromNow;
  }
  let factor = 1;
  for (let o = 1; o <= yearsFromNow; o++) {
    const rate = getInflationRatePct(pathMap, baseCalendarYear + o, flatInflationPct);
    factor *= 1 + rate / 100;
  }
  return factor;
}

/** Compound inflation from `anchorAge` through `age` (inclusive of years after anchor). */
export function inflationFactorFromAnchor({
  age,
  anchorAge,
  currentAge,
  pathMap,
  flatInflationPct,
  baseCalendarYear = new Date().getFullYear(),
}) {
  if (age <= anchorAge) return 1;
  const years = age - anchorAge;
  if (!pathMap) {
    return (1 + flatInflationPct / 100) ** years;
  }
  let factor = 1;
  const anchorCalYear = planCalendarYear(currentAge, anchorAge, baseCalendarYear);
  for (let i = 1; i <= years; i++) {
    const rate = getInflationRatePct(pathMap, anchorCalYear + i, flatInflationPct);
    factor *= 1 + rate / 100;
  }
  return factor;
}

/** Inflation multiplier for withdrawal target from retirement through `age`. */
export function withdrawalInflationFactor({
  age,
  retirementAge,
  currentAge,
  pathMap,
  flatInflationPct,
  inflateWithdrawals,
  baseCalendarYear = new Date().getFullYear(),
}) {
  if (!inflateWithdrawals || age <= retirementAge) return 1;
  const yearsIntoRetirement = age - retirementAge;
  if (!pathMap) {
    return (1 + flatInflationPct / 100) ** yearsIntoRetirement;
  }
  let factor = 1;
  const retirementCalYear = planCalendarYear(currentAge, retirementAge, baseCalendarYear);
  for (let i = 1; i <= yearsIntoRetirement; i++) {
    const rate = getInflationRatePct(pathMap, retirementCalYear + i, flatInflationPct);
    factor *= 1 + rate / 100;
  }
  return factor;
}

/**
 * Build a simulated CPIH path long enough for the retirement plan.
 * @param {{ endAge: number, currentAge: number, seed: number, baseCalendarYear?: number }} opts
 */
export function buildSimulatedInflationPath({
  endAge,
  currentAge,
  seed,
  baseCalendarYear = new Date().getFullYear(),
}) {
  const endYear = baseCalendarYear + Math.max(120, endAge - currentAge + 15);
  const result = simulateInflation({ endYear, seed });
  return {
    pathMap: buildInflationPathMap(result.projection),
    seed: result.seed,
    params: result.params,
    summary: result.summary,
  };
}
